/* Collection hero "souffle" wave: raw WebGL, no library. Flowing gold contour
   lines over the section's paper (transparent canvas), traced from a
   domain-warped noise field. Reads data-* settings (gold, intensity, speed).
   Kept light: DPR 1, capped at ~30fps, clamped time step. Setup is deferred to
   idle (which lands after the View Transition); the canvas warms up invisibly
   then fades in via a single consistent path, so the start is uniform across
   navigations. Static under reduced-motion and on low-end devices, paused
   off-screen, self-disposes when the hero re-renders (Section Rendering API). */
(function () {
  'use strict';

  var VERT = 'attribute vec2 p; void main(){ gl_Position = vec4(p, 0.0, 1.0); }';
  var FRAG = [
    'precision highp float;',
    'uniform vec2 u_res; uniform float u_time; uniform float u_gold; uniform float u_inten;',
    'float hash(vec2 p){ p = fract(p*vec2(123.34,345.45)); p += dot(p,p+34.345); return fract(p.x*p.y); }',
    'float noise(vec2 p){ vec2 i=floor(p),f=fract(p); vec2 u=f*f*(3.0-2.0*f);',
    '  float a=hash(i),b=hash(i+vec2(1,0)),c=hash(i+vec2(0,1)),d=hash(i+vec2(1,1));',
    '  return mix(mix(a,b,u.x),mix(c,d,u.x),u.y); }',
    'float fbm(vec2 p){ float v=0.0,a=0.5; for(int i=0;i<3;i++){ v+=a*noise(p); p*=2.0; a*=0.5; } return v; }',
    'void main(){',
    '  vec2 uv = gl_FragCoord.xy / u_res.xy;',
    '  float aspect = u_res.x / max(u_res.y,1.0);',
    '  vec2 p = vec2(uv.x*aspect, uv.y) * 2.4;',
    '  float t = u_time;',
    '  vec2 q = vec2(fbm(p + vec2(0.0, t*0.05)), fbm(p + vec2(5.2,1.3) - t*0.04));',
    '  float w = fbm(p*1.15 + q*0.9 + vec2(t*0.05, t*0.015));',
    '  float lf = w*2.0 - t*0.22 + uv.y*0.3;',       // x2 spacing (fewer cycles)
    '  float lg = fract(lf); float dl = min(lg, 1.0-lg);',
    '  float lw = 0.03 + 0.055*u_inten;',            // line width (intensity)
    '  float line = smoothstep(lw, 0.0, dl);',        // crisp band
    '  vec3 GOLD = vec3(0.85, 0.70, 0.35);',
    '  float a = line * u_gold;',                     // line opacity (gold % from the setting)
    '  gl_FragColor = vec4(GOLD * a, a);',           // premultiplied: floats over the paper
    '}'
  ].join('\n');

  function num(v, d) { v = parseFloat(v); return isNaN(v) ? d : v; }

  var FRAME = 1000 / 30; // cap the wave at ~30fps
  var idle = window.requestIdleCallback || function (cb) { return setTimeout(cb, 80); };

  function setup(canvas) {
    var gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    if (!gl) return; // no WebGL: the section's paper background shows through

    function sh(type, src) {
      var s = gl.createShader(type);
      gl.shaderSource(s, src); gl.compileShader(s);
      return gl.getShaderParameter(s, gl.COMPILE_STATUS) ? s : null;
    }
    var vs = sh(gl.VERTEX_SHADER, VERT), fs = sh(gl.FRAGMENT_SHADER, FRAG);
    if (!vs || !fs) return;
    var prog = gl.createProgram();
    gl.attachShader(prog, vs); gl.attachShader(prog, fs); gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) return;
    gl.useProgram(prog);

    var buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    var loc = gl.getAttribLocation(prog, 'p');
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);

    var uRes = gl.getUniformLocation(prog, 'u_res');
    var uTime = gl.getUniformLocation(prog, 'u_time');
    var uGold = gl.getUniformLocation(prog, 'u_gold');
    var uInten = gl.getUniformLocation(prog, 'u_inten');

    var d = canvas.dataset;
    var gold = num(d.gold, 100) / 100;
    var inten = num(d.inten, 50) / 100;
    var speed = num(d.speed, 20) / 100;

    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var lite = false;
    try {
      lite = (navigator.deviceMemory && navigator.deviceMemory <= 4) ||
             (navigator.hardwareConcurrency && navigator.hardwareConcurrency <= 4) ||
             (navigator.connection && navigator.connection.saveData === true) || false;
    } catch (e) {}
    var animate = !reduce && !lite; // low-end / reduced: one static frame

    var raf = null, visible = true, last = 0, tAcc = 0, ro = null, io = null, shown = false;

    // Fade in from JS, robust to any prior opacity/transition state (rapid
    // navigation can leave one mid-flight): kill the transition, commit 0, then
    // restore it and go to 1 so the fade always starts clean. CSS holds the
    // duration and the reduced-motion opt-out.
    function reveal() {
      if (shown) return;
      shown = true;
      var s = canvas.style;
      s.transition = 'none';
      s.opacity = '0';
      void canvas.offsetWidth;
      s.transition = '';
      s.opacity = '1';
    }

    function resize() {
      // DPR 1: a full-DPR full-width canvas is what dropped frames.
      var w = Math.max(1, Math.round(canvas.clientWidth));
      var h = Math.max(1, Math.round(canvas.clientHeight));
      if (w !== canvas.width || h !== canvas.height) { canvas.width = w; canvas.height = h; }
      gl.viewport(0, 0, canvas.width, canvas.height);
      render();
    }
    function render() {
      gl.uniform2f(uRes, canvas.width, canvas.height);
      gl.uniform1f(uTime, tAcc);
      gl.uniform1f(uGold, gold);
      gl.uniform1f(uInten, inten);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
    }
    function loop(now) {
      raf = null;
      if (!canvas.isConnected) { cleanup(); return; }
      if (last === 0) {
        last = now; render();                       // first frame at t=0
      } else if (now - last >= FRAME) {
        tAcc += Math.min(now - last, 100) * 0.001 * (0.3 + speed * 1.4); // clamp: no leap
        last = now; render();
      }
      if (animate && visible && !document.hidden) raf = requestAnimationFrame(loop);
    }
    function kick() {
      if (raf === null && animate && visible && !document.hidden) { last = 0; raf = requestAnimationFrame(loop); }
    }
    function cleanup() {
      if (raf) cancelAnimationFrame(raf);
      raf = null;
      if (ro) ro.disconnect();
      if (io) io.disconnect();
      document.removeEventListener('visibilitychange', onVis);
      var ext = gl.getExtension('WEBGL_lose_context');
      if (ext) ext.loseContext();
    }
    function onVis() { if (!document.hidden) kick(); }

    if (window.ResizeObserver) { ro = new ResizeObserver(resize); ro.observe(canvas); }
    else window.addEventListener('resize', resize);
    resize(); // sizes and draws the first (invisible) frame — absorbs the compile

    document.addEventListener('visibilitychange', onVis);
    if (window.IntersectionObserver) {
      io = new IntersectionObserver(function (entries) {
        visible = entries[0].isIntersecting;
        if (visible) kick();
      });
      io.observe(canvas);
    }

    if (animate) kick();
    // One consistent path (fresh load and Section-Rendering re-render alike):
    // warm invisibly, then fade in. Deferred setup already lands after the View
    // Transition, so the fade is uniform across navigations.
    setTimeout(reveal, 300);
  }

  // Defer setup to idle so the ~30ms context creation stays off the navigation
  // path; the fade-in then hides the warm-up.
  function initWave(canvas) {
    if (!canvas || canvas.dataset.bqWaveOn) return;
    canvas.dataset.bqWaveOn = '1';
    idle(function () { if (canvas.isConnected) setup(canvas); }, { timeout: 600 });
  }

  window.bqCollectionWave = initWave;

  function initAll() {
    var list = document.querySelectorAll('.bq-col-hero__wave');
    for (var i = 0; i < list.length; i++) initWave(list[i]);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();
})();
